import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { STREET_SURVEY_TEMPLATES_COLLECTION } from '@citizen-walk/shared-types';
import type {
  AiAnalysisSettings,
  AiCategoryMaster,
  AiDialogueSettings,
  AiOutputFormat,
  AnalysisDisplayConfig,
  CardPrintTemplate,
  MasterCommon,
  MasterHistoryDoc,
  ScoreDimensionMaster,
  StreetSurveyTemplate,
  SurveyPreset,
  VerificationTemplatePreset,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * マスター系コレクション CRUD hook (Phase 3.5、admin app 第 1 弾)。
 *
 * 対応 master:
 *   - M2 `aiCategoryMasters` (本 commit で初実装)
 *   - M1/M3〜M9 (本 hook を generic 化して順次対応、Phase 3.5 後続)
 *
 * 認可:
 *   - read: 認証済全員 (firestore.rules:97 isSignedIn)
 *   - create/update: master role のみ (rules:98 isMaster)
 *   - delete: 禁止 (archivedAt 論理削除のみ)
 *
 * master role 認証は別 commit (Phase 5 admin app の signInWithEmailAndPassword + setCustomUserClaims)。
 * 現状は emulator + seed script で role: 'master' を付与する前提。
 *
 * onMasterWrite trigger (a55bdee) が write 時に history 自動記録。
 */

export type MastersState<T> =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: T[]; error: null }
  | { status: 'error'; items: []; error: Error };

interface UseMastersConfig {
  /** Firestore top-level collection 名 (e.g. 'aiCategoryMasters')。 */
  collectionName: string;
  /** archivedAt が null (アーカイブ未) のみ表示 (default true)。 */
  filterArchived?: boolean;
}

function mapMasterDoc<T extends MasterCommon & { id: string }>(
  snap: QueryDocumentSnapshot<DocumentData>,
): T {
  const data = snap.data();
  return {
    ...data,
    id: snap.id,
  } as T;
}

/**
 * generic master subscribe hook。
 * displayName 昇順 + archivedAt null 優先。
 */
export function useMasters<T extends MasterCommon & { id: string }>(
  config: UseMastersConfig,
): MastersState<T> {
  const [state, setState] = useState<MastersState<T>>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    const ref = collection(db, config.collectionName);
    const q = query(ref, orderBy('displayName', 'asc'));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        let items = snap.docs.map((d) => mapMasterDoc<T>(d));
        if (config.filterArchived !== false) {
          items = items.filter((it) => !it.archivedAt);
        }
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return unsub;
  }, [config.collectionName, config.filterArchived]);

  return state;
}

/**
 * master 1 件を upsert (新規 create or 既存 update)。
 * id が指定されていれば setDoc(id)、なければ auto-id で create。
 * createdBy / createdAt は新規時のみ設定、updatedBy / updatedAt は毎回更新。
 */
export interface UpsertMasterInput<T> {
  collectionName: string;
  id?: string;
  data: Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>;
  /** master role の uid。createdBy / updatedBy に記録。 */
  uid: string;
  /** 新規作成か (true) or 既存更新か (false)。 */
  isNew: boolean;
}

export async function upsertMaster<T extends MasterCommon>(
  input: UpsertMasterInput<T>,
): Promise<string> {
  const ref = input.id
    ? doc(db, input.collectionName, input.id)
    : doc(collection(db, input.collectionName));
  const id = ref.id;

  // 新規作成は merge:false (全置換) で書くため、同 id の doc が既にあると中身が丸ごと消える。
  // 呼出側の「既存かどうか」判定は一覧 state 由来で、アーカイブ済 (一覧から除外) を取りこぼす
  // 経路があるため、書き込む直前に実データで存在確認する (無警告のデータ消失を塞ぐ最終防御)。
  // id 未指定 (auto-id) は新規 ref が採番されるので衝突しない。
  if (input.isNew && input.id) {
    const existing = await getDoc(ref);
    if (existing.exists()) {
      throw new Error(
        `同じ ID「${input.id}」の設定が既にあります（アーカイブ済のものを含みます）。` +
          `既存の設定を上書きしないよう保存を中止しました。表示名を変えて ID を採番し直すか、` +
          `既存の設定を編集してください。`,
      );
    }
  }

  const now = serverTimestamp();

  const payload: Record<string, unknown> = {
    ...input.data,
    updatedAt: now,
    updatedBy: input.uid,
  };
  if (input.isNew) {
    payload.createdAt = now;
    payload.createdBy = input.uid;
    if (!('schemaVersion' in input.data)) payload.schemaVersion = 1;
    if (!('archivedAt' in input.data)) payload.archivedAt = null;
    if (!('version' in input.data)) payload.version = 1;
    if (!('enabled' in input.data)) payload.enabled = true;
  } else {
    // update 時は以下の上書きを防止 (呼び出し側が data に含めて渡しても merge 時に既存値を残す):
    //   - schemaVersion: create 時のみ設定、以降は migration script のみが書込
    //   - archivedAt: archive 時のみ設定 (archiveMaster 経由)
    //   - version: 自動 increment は R-29 で起票済、暫定で update 時は既存値保持
    delete payload.schemaVersion;
    delete payload.archivedAt;
    delete payload.version;
  }

  await setDoc(ref, payload, { merge: !input.isNew });
  return id;
}

/**
 * master を archivedAt = serverTimestamp() で論理削除 (rules で物理 delete 禁止)。
 */
export async function archiveMaster(
  collectionName: string,
  id: string,
  uid: string,
): Promise<void> {
  const ref = doc(db, collectionName, id);
  await setDoc(
    ref,
    {
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}

/**
 * archive を解除して復元する (archivedAt = null)。archiveMaster の対。
 * 統合エディタのテンプレ一覧から「アーカイブ済 → 有効」へ戻す用 (filterArchived 既定の一覧に再表示される)。
 */
export async function unarchiveMaster(
  collectionName: string,
  id: string,
  uid: string,
): Promise<void> {
  const ref = doc(db, collectionName, id);
  await setDoc(
    ref,
    {
      archivedAt: null,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
    },
    { merge: true },
  );
}

/**
 * 緊急用: master を物理削除。
 * firestore.rules の `allow delete: if false` で client 実行は必ず deny される。
 * 本関数は参照実装 (型 / 呼び出し方) で、実運用は Admin SDK (functions) 経由で行う。
 * 通常は archiveMaster を使う。
 */
export async function _hardDeleteMaster(collectionName: string, id: string): Promise<void> {
  await deleteDoc(doc(db, collectionName, id));
}

/**
 * master の history (/<collection>/{id}/history) を changedAt desc で subscribe。
 * 表示用に最大 50 件まで取得 (Phase 5 で pagination 検討、R-29 後続)。
 * firestore.rules: read は master role のみ、write は trigger 専用 (client deny)。
 *
 * masterId が null の間は subscribe 開始しない (master 未選択時用)。
 */
export type MasterHistoryEntry = MasterHistoryDoc & { id: string };

export function useMasterHistory(
  collectionName: string,
  masterId: string | null,
): MastersState<MasterHistoryEntry> {
  const [state, setState] = useState<MastersState<MasterHistoryEntry>>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    // 同期 setState を直接呼ぶと react-hooks/set-state-in-effect の警告対象、
    // 既存 features (useDistrictResolver) と同じ wrapper パターンで吸収する。
    function applyState(next: MastersState<MasterHistoryEntry>) {
      setState(next);
    }
    if (!masterId) {
      applyState({ status: 'loading', items: [], error: null });
      return;
    }
    const ref = collection(db, collectionName, masterId, 'history');
    const q = query(ref, orderBy('changedAt', 'desc'), limit(50));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({
          ...(d.data() as MasterHistoryDoc),
          id: d.id,
        }));
        applyState({ status: 'ready', items, error: null });
      },
      (error) => applyState({ status: 'error', items: [], error }),
    );
    return unsub;
  }, [collectionName, masterId]);

  return state;
}

/**
 * 各 master 専用の型付き hook (collectionName を 1 箇所に固定)。
 * generic useMasters を 1:1 で呼ぶだけの薄い wrapper。
 */

/** master 専用 hook の共通オプション。 */
export interface MasterHookOptions {
  /**
   * アーカイブ済も含めて購読する (既定 false = アーカイブ済を除外)。
   *
   * 新規保存は merge:false の全置換なので、ドキュメント ID の重複判定には**アーカイブ済も
   * 含めた全 id** が要る (アーカイブ済と同 id で保存するとそのマスタを無警告で上書き消滅させる)。
   * 使用状況の集計も、アーカイブ済からの参照を取りこぼさないよう全件で行う。
   */
  includeArchived?: boolean;
}
export function useAiCategoryMasters(): MastersState<AiCategoryMaster & { id: string }> {
  return useMasters<AiCategoryMaster & { id: string }>({
    collectionName: 'aiCategoryMasters',
  });
}

export function useAiOutputFormats(
  options?: MasterHookOptions,
): MastersState<AiOutputFormat & { id: string }> {
  return useMasters<AiOutputFormat & { id: string }>({
    collectionName: 'aiOutputFormats',
    filterArchived: !options?.includeArchived,
  });
}

export function useAnalysisDisplayConfigs(
  options?: MasterHookOptions,
): MastersState<AnalysisDisplayConfig & { id: string }> {
  return useMasters<AnalysisDisplayConfig & { id: string }>({
    collectionName: 'analysisDisplayConfigs',
    filterArchived: !options?.includeArchived,
  });
}

export function useVerificationTemplatePresets(): MastersState<
  VerificationTemplatePreset & { id: string }
> {
  return useMasters<VerificationTemplatePreset & { id: string }>({
    collectionName: 'verificationTemplatePresets',
  });
}

/** 机上調査テンプレート (docs/27 §17)。 */
export function useStreetSurveyTemplates(
  options?: MasterHookOptions,
): MastersState<StreetSurveyTemplate & { id: string }> {
  return useMasters<StreetSurveyTemplate & { id: string }>({
    collectionName: STREET_SURVEY_TEMPLATES_COLLECTION,
    filterArchived: !options?.includeArchived,
  });
}

export function useSurveyPresets(): MastersState<SurveyPreset & { id: string }> {
  return useMasters<SurveyPreset & { id: string }>({
    collectionName: 'surveyPresets',
  });
}

export function useAiAnalysisSettings(
  options?: MasterHookOptions,
): MastersState<AiAnalysisSettings & { id: string }> {
  return useMasters<AiAnalysisSettings & { id: string }>({
    collectionName: 'aiAnalysisSettings',
    filterArchived: !options?.includeArchived,
  });
}

export function useAiDialogueSettings(): MastersState<AiDialogueSettings & { id: string }> {
  return useMasters<AiDialogueSettings & { id: string }>({
    collectionName: 'aiDialogueSettings',
  });
}

export function useScoreDimensionMasters(): MastersState<ScoreDimensionMaster & { id: string }> {
  return useMasters<ScoreDimensionMaster & { id: string }>({
    collectionName: 'scoreDimensionMasters',
  });
}

export function useCardPrintTemplates(): MastersState<CardPrintTemplate & { id: string }> {
  return useMasters<CardPrintTemplate & { id: string }>({
    collectionName: 'cardPrintTemplates',
  });
}
